import { LineaVenta } from "../models/LineaVentaModel";
import { UserPayload } from "../models/SiteModel";
import { parseDecimalValue } from "../utils/NumberParser";

const URLAPI = process.env.REACT_APP_BASE_URL;

export type PedidoEntregaInput = {
  idLocal: number;
  idCliente: number;
  cliente: string;
  fechaEntrega: string;
  horaDesde: string;
  horaHasta: string;
  observacion: string;
  retiraDelLocal: boolean;
  sucursalEntrega: string;
  ldp?: string;
  origen?: string;
};

export type PedidoClienteDto = {
  idClientePedido: number;
  idLocal: number;
  idCliente: number;
  cliente: string;
  telefono?: string;
  celular?: string;
  fecha: string;
  fechaEntrega: string;
  horaDesde: string;
  horaHasta: string;
  observacion: string;
  retiraDelLocal: boolean;
  sucursalEntrega: string;
  total: number;
  origen: string;
};

export type PedidoClienteLineaDto = {
  idProducto: number;
  producto: string;
  cantidad: number;
  unitario: number;
};

export type PedidoConDetalleDto = {
  clientePedido: PedidoClienteDto;
  detalle: PedidoClienteLineaDto[];
};

const toDateTime = (fecha: string, hora: string) => `${fecha}T${hora || "00:00"}:00`;

const getUnitario = (linea: LineaVenta) => {
  const producto = linea.producto as any;
  return parseDecimalValue(producto.CONTADO ?? producto.contado ?? producto.Contado ?? producto.precio ?? producto.Precio ?? 0);
};

const getProductoId = (linea: LineaVenta) => {
  const producto = linea.producto as any;
  return Number(producto.IDPRODUCTO ?? producto.IdProducto ?? producto.idProducto ?? producto.id_producto ?? 0);
};

const getProductoNombre = (linea: LineaVenta) => {
  const producto = linea.producto as any;
  return String(producto.PRODUCTO ?? producto.Producto ?? producto.producto ?? "");
};

export async function fetchCrearPedido(user: UserPayload, pedido: PedidoEntregaInput, lineas: LineaVenta[]) {
  const detalle = lineas.map((linea) => ({
    idDetalle: 0,
    idClientePedido: 0,
    idLocal: pedido.idLocal,
    idProducto: getProductoId(linea),
    producto: getProductoNombre(linea),
    cantidad: linea.cantidad,
    unitario: getUnitario(linea),
  }));

  const response = await fetch(`${URLAPI}/Pedido/Crear`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "Authorization": "Bearer " + user.token,
    },
    body: JSON.stringify({
      pedido: {
        idClientePedido: 0,
        idLocal: pedido.idLocal,
        idEmpleado: 0,
        idCliente: pedido.idCliente,
        cliente: pedido.cliente,
        fechaEntrega: toDateTime(pedido.fechaEntrega, pedido.horaDesde),
        fechaEntregado: null,
        idUsuario: 0,
        idEstado: 1,
        observacion: pedido.observacion,
        horaDesde: toDateTime(pedido.fechaEntrega, pedido.horaDesde),
        horaHasta: toDateTime(pedido.fechaEntrega, pedido.horaHasta),
        retiraDelLocal: pedido.retiraDelLocal,
        sucursalEntrega: pedido.retiraDelLocal ? "" : pedido.sucursalEntrega,
        ldp: pedido.ldp ?? "",
        origen: pedido.origen ?? "",
      },
      detalle,
    }),
  });

  const data = await response.json();
  if (!response.ok) {
    throw data;
  }

  return data;
}

const normalizePedido = (item: any): PedidoConDetalleDto => {
  const pedido = item?.clientePedido ?? item?.ClientePedido ?? {};
  const detalle = item?.detalle ?? item?.Detalle ?? [];
  return {
    clientePedido: {
      idClientePedido: pedido.idClientePedido ?? pedido.IdClientePedido ?? 0,
      idLocal: pedido.idLocal ?? pedido.IdLocal ?? 0,
      idCliente: pedido.idCliente ?? pedido.IdCliente ?? 0,
      cliente: pedido.cliente ?? pedido.Cliente ?? "",
      telefono: pedido.telefono ?? pedido.Telefono ?? "",
      celular: pedido.celular ?? pedido.Celular ?? "",
      fecha: pedido.fecha ?? pedido.Fecha ?? "",
      fechaEntrega: pedido.fechaEntrega ?? pedido.FechaEntrega ?? "",
      horaDesde: pedido.horaDesde ?? pedido.HoraDesde ?? "",
      horaHasta: pedido.horaHasta ?? pedido.HoraHasta ?? "",
      observacion: pedido.observacion ?? pedido.Observacion ?? "",
      retiraDelLocal: pedido.retiraDelLocal ?? pedido.RetiraDelLocal ?? false,
      sucursalEntrega: pedido.sucursalEntrega ?? pedido.SucursalEntrega ?? "",
      total: parseDecimalValue(pedido.total ?? pedido.Total ?? 0),
      origen: pedido.origen ?? pedido.Origen ?? "",
    },
    detalle: Array.isArray(detalle) ? detalle.map((linea: any) => ({
      idProducto: linea.idProducto ?? linea.IdProducto ?? 0,
      producto: linea.producto ?? linea.Producto ?? "",
      cantidad: parseDecimalValue(linea.cantidad ?? linea.Cantidad ?? 0),
      unitario: parseDecimalValue(linea.unitario ?? linea.Unitario ?? 0),
    })) : [],
  };
};

export async function fetchUltimosPedidosCliente(user: UserPayload, idCliente: number) {
  const response = await fetch(`${URLAPI}/Pedido/UltimosPorCliente/${idCliente}`, {
    method: "GET",
    headers: {
      "Authorization": "Bearer " + user.token,
    },
  });

  if (!response.ok) {
    let message = "No se pudieron obtener los ultimos pedidos del cliente";
    try {
      const data = await response.json();
      message = data?.message ?? message;
    } catch { }
    throw new Error(message);
  }

  const data = await response.json();
  return Array.isArray(data) ? data.map(normalizePedido) : [];
}

export async function fetchMisPedidosCliente(user: UserPayload) {
  const response = await fetch(`${URLAPI}/Pedido/MisPedidos`, {
    method: "GET",
    headers: {
      "Authorization": "Bearer " + user.token,
    },
  });

  if (!response.ok) {
    let message = "No se pudieron obtener sus pedidos";
    try {
      const data = await response.json();
      message = data?.message ?? message;
    } catch { }
    throw new Error(message);
  }

  const data = await response.json();
  return Array.isArray(data) ? data.map(normalizePedido) : [];
}

export async function fetchDescargarPedidoPdf(user: UserPayload, idLocal: number, idPedido: number) {
  const response = await fetch(`${URLAPI}/Pedido/DescargarPdf/${idLocal}/${idPedido}`, {
    method: "GET",
    headers: {
      "Authorization": "Bearer " + user.token,
    },
  });

  if (!response.ok) {
    let message = "No se pudo descargar el PDF del pedido";
    try {
      const data = await response.json();
      message = data?.message ?? message;
    } catch { }
    throw new Error(message);
  }

  const blob = await response.blob();
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = `Pedido_${idPedido}.pdf`;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
}
